import styled from "@emotion/styled"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { cssVarCurBanHeight, cssVarExpansion, cssVarHeight, cssVarTransitionDelay, cssVarTransitionDuration } from "contexts/Header"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import getMediaQuery from "css/breakpoints"
import { bold } from "css/text"
import useSSR from "hooks/useSSR"
import { useLayoutEffect, useRef } from "react"
import { getOriginLevel2, sendWatchGridEvent } from "./events"
import { getCurrentUrl, getParamsPerFacet } from "./utils"
import { buttonIcon, translucentDark } from "css/buttons"
import { Icon } from "components/icon/Icon"
import { useMotionValue, useScroll } from "framer-motion"
import { mvSubscribe } from "components/model/model-variations/context"

const Root = styled.div`
  --subnav-ofs: calc(var(--subnav-height) * var(--is-sub));
  position: sticky;
  top: calc(((var(${cssVarHeight}) + var(${cssVarCurBanHeight})) * var(${cssVarExpansion}) + var(--subnav-ofs)));
  transition: top var(${cssVarTransitionDuration}) var(${cssVarTransitionDelay});
  z-index: 10;
  background: rgb(var(--pure-white));
  margin-inline: auto;
  margin-block-end: 20px;
  width: calc(var(--vw100, 100vh) - 2 * var(--outer-margin));
  max-width: 120rem;
  display: grid;
  justify-content: center;
  --arrow-size: 30px;
  --item-disappear: 16px;

  &[aria-hidden="true"] ul {
    display: none;
  }

  ${getMediaQuery("m")} {
    margin-block-end: 40px;
  }
`

const List = styled.ul`
  position: relative;
  display: inline-flex;
  justify-content: start;
  flex-direction: row;
  scroll-snap-type: x mandatory;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-padding-inline: var(--arrow-size);

  &::before,
  &::after {
    //    content: "";
    flex-shrink: 0;
    position: relative;
    width: var(--outer-margin);
  }

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  mask-image: linear-gradient(90deg, rgb(0 0 0 / 0.2), rgb(0 0 0 / 1) 30%, rgb(0 0 0 / 1) 70%, rgb(0 0 0 / 0.2) 100%);

  &.prevonly.nextonly {
    mask-image: unset;
  }
  &.prevonly {
    mask-image: linear-gradient(90deg, rgb(0 0 0 / 0.2), rgb(0 0 0 / 1) 30%, rgb(0 0 0 / 1) 100%);
    [dir="rtl"] & {
      mask-image: linear-gradient(90deg, rgb(0 0 0 / 1), rgb(0 0 0 / 1) 70%, rgb(0 0 0 / 0.2) 100%);
    }
  }
  &.nextonly {
    mask-image: linear-gradient(90deg, rgb(0 0 0 / 1), rgb(0 0 0 / 1) 70%, rgb(0 0 0 / 0.2) 100%);
    [dir="rtl"] & {
      mask-image: linear-gradient(90deg, rgb(0 0 0 / 0.2), rgb(0 0 0 / 1) 30%, rgb(0 0 0 / 1) 100%);
    }
  }

  ${getMediaQuery("m")} {
    mask-image: unset !important;
  }
`

const Item = styled.li`
  scroll-snap-align: start;
  padding-inline: 24px 16px;
  flex-shrink: 0;
  position: relative;

  &:first-of-type {
    padding-inline: 0;
  }
  &:first-of-type {
    padding-inline: 0px 16px;
  }
`

const Label = styled.label`
  display: block;
  font-size: 16px;
  line-height: 80px;
  ${bold}
  cursor: pointer;
  user-select: none;
  transition: color 0.3s;
  overflow: hidden;

  &:hover {
    color: rgb(var(--green));
  }

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    margin-block-start: -4px;
    margin-inline-start: 8px;
    width: 8px;
    height: 8px;
    border-radius: 8px;
    background: rgb(var(--green));
    opacity: 0;
    transition: opacity 0.5s;
  }

  ${getMediaQuery("xl")} {
    &:after {
      margin-inline-start: 6px;
    }
  }
`

const Input = styled.input`
  position: absolute;
  top: -10px;
  left: -10px;
  opacity: 0;

  &:checked + label {
    color: rgb(var(--green));

    &:after {
      opacity: 1;
    }
  }

  &.focus-visible + label {
    outline: 2px solid rgb(var(--focus, 0 255 255) / 1);
    outline-offset: -2px;
  }
`

const Arrows = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: none;
  flex-direction: row;
  pointer-events: none;
  justify-content: space-between;
  grid-row: 1/-1;

  ${getMediaQuery("m")} {
    display: flex;
  }

  & > div {
    position: relative;
    width: calc(var(--arrow-size) + var(--item-disappear));
    display: flex;
    align-items: center;
    box-sizing: border-box;

    &.previous {
      background: white;
      mask-image: linear-gradient(90deg, black var(--arrow-size), transparent);
      -webkit-mask-image: linear-gradient(90deg, black var(--arrow-size), transparent);
      justify-self: start;
      justify-content: start;

      svg {
        transform: scale(-1);
      }
      [dir="rtl"] &.previous {
        mask-image: linear-gradient(270deg, black var(--arrow-size), transparent);
        -webkit-mask-image: linear-gradient(270deg, black var(--arrow-size), transparent);
        & svg {
          transform: scale(1);
        }
      }
    }
    &.next {
      background: white;
      mask-image: linear-gradient(270deg, black var(--arrow-size), transparent);
      -webkit-mask-image: linear-gradient(270deg, black var(--arrow-size), transparent);
      justify-self: end;
      justify-content: end;

      [dir="rtl"] &.next {
        mask-image: linear-gradient(90deg, black var(--arrow-size), transparent);
        -webkit-mask-image: linear-gradient(90deg, black var(--arrow-size), transparent);
        & svg {
          transform: scale(-1);
        }
      }
    }
    &.qf-arrow-hidden {
      visibility: hidden;
    }
  }
`

const Arrow = styled.button`
  position: relative;
  z-index: 1;
  ${buttonIcon}
  ${translucentDark}
  pointer-events: auto;
  border: 0;
  border-radius: 50%;

  width: var(--arrow-size);
  height: var(--arrow-size);

  transform: translateX(0%);
  transition: transform 0.6s cubic-bezier(0.33, 1, 0.68, 1) 1.2s;

  ${getMediaQuery("m")} {
    --arrow-size: 30px;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

function QuickFiltersArrows(props) {
  const { scrollto, obs, edgewidth } = props
  const rfedge = useRef(null)
  const rfmain = useRef(null)

  function onPrevious(e) {
    scrollto(-1)
  }
  function onNext(e) {
    scrollto(1)
  }

  function onObs(v) {
    const states = v.split(",").map(Number)
    ;[...rfmain.current.querySelectorAll("div")].map((el, i) => {
      const state = !!states[i]
      el.classList[!state ? "add" : "remove"]("qf-arrow-hidden")
      el.setAttribute("aria-hidden", !state)
    })
  }
  useLayoutEffect(() => mvSubscribe("", obs, onObs), [])

  useLayoutEffect(() => {
    window.addEventListener("resize", onResize, false)
    onResize()

    return () => window.removeEventListener("resize", onResize, false)

    function onResize() {
      edgewidth.set(Math.round(rfedge.current.offsetWidth))
    }
  }, [])

  return (
    <Arrows ref={rfmain}>
      <div className='previous qf-arrow-hidden' aria-hidden='true' ref={rfedge}>
        <Arrow onClick={onPrevious} tabIndex='-1'>
          <Icon type={"moveNext"} />
        </Arrow>
      </div>
      <div className='next qf-arrow-hidden' aria-hidden='true'>
        <Arrow onClick={onNext} tabIndex='-1'>
          <Icon type={"moveNext"} />
        </Arrow>
      </div>
    </Arrows>
  )
}

export default function QuickFilters({ quickFilters, searchHubs, pipelines, searchUids, gridData, disable, category = {} }) {
  const ssr = useSSR()
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const env = useEnv()
  const locale = useLocale()

  const rootRef = useRef()
  const listRef = useRef()

  const QFFacet = quickFilters[0].facet

  const facetValues = getParamsPerFacet(QFFacet)

  const onFilterChanged = e => {
    const { name, value } = e.target

    const currentUrl = getCurrentUrl()

    const params = currentUrl.searchParams

    if (value === "*") {
      params.delete(name)
      category.current = "all"
    } else {
      params.set(name, value)
      category.current = value
    }

    // coveo event
    searchUids.forEach((searchUid, index) => {
      const token = pipelines[index]

      sendWatchGridEvent(env.organizationId, token, "search", {
        actionCause: "interfaceChange",
        language: locale?.current?.codes?.www,
        numberOfResults: gridData.length,
        queryText: "",
        responseTime: 0,
        searchQueryUid: searchUid,
        searchHub: searchHubs[index],
        anonymous: true,
        originLevel1: searchHubs[index],
        originLevel2: getOriginLevel2(name === "global_category" ? value : params.get("global_category")),
        userAgent: navigator?.userAgent,
      })
    })

    navigation.push(currentUrl.href, { nopageview: true }, { scroll: false })
  }

  const { scrollX } = useScroll({
    container: listRef,
    axis: "x",
    offset: ["start end", "end end"],
  })

  useLayoutEffect(() => {
    const options = {
      root: rootRef.current,
      rootMargin: "0px -30px 0px -24px",
      threshold: 1.0,
    }

    const intersectionCallback = entries => {
      entries.forEach(entry => {
        const elem = entry.target
        const state = entry.isIntersecting ? +(entry.intersectionRatio >= 1) : 0
        elem.setAttribute("data-inter", state)
      })
    }

    const observer = new IntersectionObserver(intersectionCallback, options)

    const col = [...listRef.current.childNodes]
    col.forEach((el, i) => {
      observer.observe(el)
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  function scrollto(v) {
    const rtl = process.browser && document.querySelector("html").getAttribute("dir") === "rtl"
    const W = listRef.current.offsetWidth
    const col = [...listRef.current.childNodes]
    let prev = null
    let next = null
    for (const el of col) {
      const inter = +el.getAttribute("data-inter")
      if (!inter && !next) prev = el
      if (!inter && next) {
        next = el
        break
      }
      if (inter) next = el
    }
    if (v > 0) {
      if (rtl) {
        listRef.current.scrollLeft = next?.offsetLeft + next?.offsetWidth - W + edgewidth.get()
      } else {
        listRef.current.scrollLeft = next?.offsetLeft
      }
    } else {
      if (rtl) {
        listRef.current.scrollLeft = prev?.offsetLeft
      } else {
        listRef.current.scrollLeft = -W + prev?.offsetLeft + prev?.offsetWidth + edgewidth.get()
      }
    }
  }

  const obs = useMotionValue("0,0")

  function onScroll(x) {
    const offset = 2
    const rtl = process.browser && document.querySelector("html").getAttribute("dir") === "rtl"
    const scrollmax = listRef.current.scrollWidth - listRef.current.offsetWidth

    if (rtl) {
      listRef.current.classList[x >= -offset ? "add" : "remove"]("nextonly")
      listRef.current.classList[x <= -scrollmax + offset ? "add" : "remove"]("prevonly")
    } else {
      listRef.current.classList[x <= offset ? "add" : "remove"]("nextonly")
      listRef.current.classList[x >= scrollmax - offset ? "add" : "remove"]("prevonly")
    }
  }
  useLayoutEffect(() => mvSubscribe("", scrollX, onScroll), [])

  function updateArrowStates(x) {
    const offset = 2
    const rtl = process.browser && document.querySelector("html").getAttribute("dir") === "rtl"
    const scrollmax = listRef.current.scrollWidth - listRef.current.offsetWidth
    obs.set([rtl ? x < 0 : x > 0, rtl ? x - offset > -scrollmax : x + offset < scrollmax].map(Number).join(","))
  }

  useLayoutEffect(() => mvSubscribe("", scrollX, updateArrowStates), [])

  const edgewidth = useMotionValue(0)

  return (
    <Root ref={rootRef} aria-hidden={disable} style={{ "--is-sub": !ssr && document.querySelector(".header-subnav") ? 1 : 0 }}>
      <List ref={listRef} aria-label={dictionary.ariaLabelQuickfiltersList()}>
        {quickFilters.map((item, i) => {
          let checkedValue = false

          if (facetValues.length === 0 && item.value === "*") {
            checkedValue = true
            category.current = "all"
          } else if (facetValues.length > 0 && facetValues[0] === item.value) {
            checkedValue = true
            category.current = item.value
          }

          return (
            <Item key={i}>
              <Input type='radio' id={`quickfilters-radio-${i}`} name={item.facet} value={item.value} onChange={onFilterChanged} checked={checkedValue} />
              <Label htmlFor={`quickfilters-radio-${i}`}>{item.localizedValue}</Label>
            </Item>
          )
        })}
      </List>
      <QuickFiltersArrows scrollto={scrollto} obs={obs} edgewidth={edgewidth} />
    </Root>
  )
}
